import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, FileText, Image as ImageIcon, File } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { doctorAPI, fileAPI } from '../utils/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function CaseFilesViewer() {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFiles();
  }, [caseId]);

  const fetchFiles = async () => {
    try {
      const response = await doctorAPI.getCaseFiles(caseId);
      setFiles(response.data.files);
    } catch (error) {
      toast.error('Failed to load files');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (fileId, filename) => {
    try {
      const response = await fileAPI.download(fileId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('File downloaded');
    } catch (error) {
      toast.error('Failed to download file');
    }
  };

  const getFileIcon = (contentType) => {
    if (contentType.startsWith('image/')) {
      return <ImageIcon className="w-8 h-8 text-blue-600" />;
    } else if (contentType === 'application/pdf') {
      return <FileText className="w-8 h-8 text-red-600" />;
    } else {
      return <File className="w-8 h-8 text-slate-600" />;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  return (
    <div className="min-h-screen bg-slate-50 py-8">
      <div className="max-w-6xl mx-auto px-4">
        <Button
          onClick={() => navigate('/doctor')}
          variant="ghost"
          className="mb-6 text-slate-600 hover:text-slate-900"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Cases
        </Button>

        <Card className="p-8 bg-white rounded-xl border border-slate-100 shadow-sm">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold font-heading text-slate-900">Case Files</h2>
              <p className="text-slate-600 mt-1">Case ID: {caseId}</p>
            </div>
            <div className="text-sm text-slate-500">
              {files.length} file{files.length !== 1 ? 's' : ''}
            </div>
          </div>

          {loading ? (
            <div className="text-center py-12 text-slate-600">Loading files...</div>
          ) : files.length === 0 ? (
            <div className="text-center py-12">
              <File className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600">No files uploaded yet</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {files.map((file) => (
                <Card
                  key={file.file_id}
                  className="p-4 bg-slate-50 rounded-lg border border-slate-200 hover:shadow-md transition-all group"
                >
                  <div className="flex items-start gap-3 mb-3">
                    {getFileIcon(file.content_type)}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-slate-900 truncate text-sm">
                        {file.filename}
                      </h3>
                      <p className="text-xs text-slate-500 mt-1">
                        {formatFileSize(file.size)}
                      </p>
                    </div>
                  </div>

                  {file.content_type.startsWith('image/') && (
                    <div className="mb-3 rounded-lg overflow-hidden bg-white border border-slate-200">
                      <img
                        src={`${BACKEND_URL}/api/files/${file.file_id}`}
                        alt={file.filename}
                        className="w-full h-32 object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  )}

                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleDownload(file.file_id, file.filename)}
                      className="flex-1 bg-teal-600 text-white hover:bg-teal-700 rounded-lg text-xs h-8"
                    >
                      <Download className="w-3 h-3 mr-1" />
                      Download
                    </Button>
                    {(file.content_type.startsWith('image/') || file.content_type === 'application/pdf') && (
                      <Button
                        onClick={() => window.open(`${BACKEND_URL}/api/files/${file.file_id}`, '_blank')}
                        variant="outline"
                        className="rounded-lg text-xs h-8 px-3"
                      >
                        View
                      </Button>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
